/**
 * The reminder to save a backup (todo 14): Safari may delete a website's data, and an uninstalled Android app loses
 * it, so after every few finished workouts without a backup the Workout tab asks for one. When a backup was saved (or
 * restored) and when the reminder was put off is kept in the META record "backup"; nothing else is stored.
 */
import { META, type Backend } from "./storage";

/** Remind after this many finished workouts since the last backup (or since "Später"). */
export const REMIND_EVERY = 5;

export interface BackupState {
  /** last "Backup speichern" that saved a file, or a restore (the data then equals a file) */
  savedAt?: number;
  /** last "Später" on the reminder */
  snoozedAt?: number;
}

type BackupRecord = BackupState & { id: "backup" };
const ID = "backup";

export async function loadBackupState(db: Backend): Promise<BackupState> {
  const { id: _, ...state } = (await db.get<BackupRecord>(META, ID)) ?? { id: ID };
  return state;
}

async function update(db: Backend, change: BackupState) {
  await db.put(META, { ...(await loadBackupState(db)), ...change, id: ID } as BackupRecord);
}

export const markBackupSaved = (db: Backend, now: number) => update(db, { savedAt: now });
export const snoozeBackupReminder = (db: Backend, now: number) => update(db, { snoozedAt: now });

/**
 * The number of workouts finished since the last backup when the reminder is due, otherwise undefined. Due once
 * `REMIND_EVERY` workouts were finished after both the last backup and the last "Später".
 */
export function backupReminder(sessions: { endedAt: number }[], state: BackupState): number | undefined {
  const since = (t: number) => sessions.filter((s) => s.endedAt > t).length;
  const saved = state.savedAt ?? -Infinity;
  return since(Math.max(saved, state.snoozedAt ?? -Infinity)) >= REMIND_EVERY ? since(saved) : undefined;
}
